import { useState } from 'react';
import { createUser } from '../../../api/user';
import { validatePassword, PASSWORD_HINT } from '../../../utils/passwordRules';

interface UserFormModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

/**
 * 管理员新建用户弹窗。
 * 后端 POST /users：username / fullName / phone 必填，email / password / role 可选。
 * 初始密码留空表示该账号尚无密码，由用户走「激活 / 忘记密码」流程自行设置。
 */
export default function UserFormModal({ open, onClose, onSuccess }: UserFormModalProps) {
  const [form, setForm] = useState({
    username: '',
    fullName: '',
    phone: '',
    email: '',
    password: '',
    role: 'user',
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  if (!open) return null;

  const set = (key: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
    setForm((prev) => ({ ...prev, [key]: e.target.value }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!form.username.trim() || !form.fullName.trim() || !form.phone.trim()) {
      setError('用户名、姓名、手机号为必填项');
      return;
    }
    if (!/^[a-zA-Z0-9_]{3,20}$/.test(form.username.trim())) {
      setError('用户名为 3-20 位字母、数字或下划线');
      return;
    }
    if (!/^1[3-9]\d{9}$/.test(form.phone.trim())) {
      setError('请输入正确的 11 位手机号');
      return;
    }
    if (form.password) {
      const passwordError = validatePassword(form.password);
      if (passwordError) {
        setError(passwordError);
        return;
      }
    }
    setSubmitting(true);
    try {
      await createUser({
        username: form.username.trim(),
        fullName: form.fullName.trim(),
        phone: form.phone.trim(),
        email: form.email.trim() || undefined,
        password: form.password || undefined,
        role: form.role,
      });
      onSuccess();
      onClose();
      // 重置表单
      setForm({ username: '', fullName: '', phone: '', email: '', password: '', role: 'user' });
    } catch (err) {
      setError(err instanceof Error ? err.message : '创建失败，请稍后重试');
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls =
    'w-full px-3 py-2.5 text-sm border border-gray-200 rounded-xl bg-white focus:outline-none focus:border-teal-400 focus:ring-1 focus:ring-teal-400 transition-colors';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* 遮罩 */}
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      {/* 弹窗主体 */}
      <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <h2 className="text-base font-semibold text-gray-900">新建用户</h2>
          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-600 cursor-pointer transition-colors"
          >
            <i className="ri-close-line text-lg" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          {error && (
            <div className="flex items-center gap-2 px-3 py-2.5 text-sm text-red-600 bg-red-50 rounded-xl">
              <i className="ri-error-warning-line" />
              <span>{error}</span>
            </div>
          )}

          <div>
            <label className="block text-sm text-gray-600 mb-1.5">
              用户名 <span className="text-red-400">*</span>
            </label>
            <input value={form.username} onChange={set('username')} placeholder="登录账号（3-20 位字母、数字或下划线）" className={inputCls} />
          </div>

          <div>
            <label className="block text-sm text-gray-600 mb-1.5">
              姓名 <span className="text-red-400">*</span>
            </label>
            <input value={form.fullName} onChange={set('fullName')} placeholder="真实姓名" className={inputCls} />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-gray-600 mb-1.5">
                手机号 <span className="text-red-400">*</span>
              </label>
              <input value={form.phone} onChange={set('phone')} placeholder="11 位手机号" className={inputCls} />
            </div>
            <div>
              <label className="block text-sm text-gray-600 mb-1.5">邮箱</label>
              <input value={form.email} onChange={set('email')} placeholder="name@example.com（选填）" className={inputCls} />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-gray-600 mb-1.5">初始密码</label>
              <input
                type="password"
                value={form.password}
                onChange={set('password')}
                placeholder="留空由用户自行激活"
                className={inputCls}
              />
              <p className="text-xs text-gray-400 mt-1">填写时{PASSWORD_HINT}</p>
            </div>
            <div>
              <label className="block text-sm text-gray-600 mb-1.5">角色</label>
              <select value={form.role} onChange={set('role')} className={inputCls}>
                <option value="user">普通用户</option>
                <option value="admin">管理员</option>
              </select>
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2.5 text-sm text-gray-600 border border-gray-200 rounded-xl hover:border-teal-300 hover:text-teal-600 cursor-pointer transition-colors"
            >
              取消
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="px-4 py-2.5 bg-teal-500 hover:bg-teal-600 text-white text-sm font-medium rounded-xl cursor-pointer transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting ? '创建中...' : '创建用户'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}